import groupBy from 'lodash/groupBy';

import type { ConditionViolationsStatus } from '@/warranty/domain/entities/condition-violation-status';
import {
  type ConditionsViolationTimeseriesData,
  type TimeseriesPoint,
} from '@/warranty/domain/entities/condition-violation-timeseries';

export function aggregateConditionViolationTimeseriesToTopLevel(
  units: ConditionsViolationTimeseriesData[],
): ConditionsViolationTimeseriesData[] {
  // First group by top ID
  const groupedByTop = groupBy(units, (item) => item.topFullId);

  const results: ConditionsViolationTimeseriesData[] = [];

  for (const [topFullId, items] of Object.entries(groupedByTop)) {
    const { groupFullId } = items[0];
    const fullFrontendId = topFullId;
    const unitFullId = undefined;

    // Subgroup by condition violation type
    const byDescription = groupBy(items, (item) => item.description);

    for (const [description, entries] of Object.entries(byDescription)) {
      // Map from timestamp (ISO string) -> count sum
      const timeseriesMap = new Map<string, number>();

      for (const entry of entries) {
        for (const point of entry.timeseries) {
          const key = point.timestamp.toISOString();
          const existing = timeseriesMap.get(key) || 0;
          timeseriesMap.set(key, existing + point.count);
        }
      }

      // Convert back to TimeseriesPoint[]
      const aggregatedTimeseries: TimeseriesPoint[] = Array.from(
        timeseriesMap.entries(),
      ).map(([timestampStr, count]) => ({
        timestamp: new Date(timestampStr),
        count,
      }));

      results.push({
        id: `${topFullId}__${description}`, // composed ID to keep it unique
        groupFullId,
        topFullId,
        unitFullId,
        fullFrontendId,
        description:
          description as ConditionsViolationTimeseriesData['description'],
        timeseries: aggregatedTimeseries.sort(
          (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
        ),
      });
    }
  }

  return results;
}

export function aggregateConditionViolationStatusToTopLevel(
  units: ConditionViolationsStatus[],
): ConditionViolationsStatus[] {
  const groupedByTop = groupBy(units, (item) => item.topFullId);

  const results: ConditionViolationsStatus[] = [];

  for (const [topFullId, items] of Object.entries(groupedByTop)) {
    const { groupFullId } = items[0];
    const fullFrontendId = topFullId;
    const unitFullId = undefined;

    // Subgroup by violation type
    const byDescription = groupBy(items, (item) => item.description);

    for (const [description, entries] of Object.entries(byDescription)) {
      results.push({
        id: `${topFullId}__${description}`,
        groupFullId,
        topFullId,
        unitFullId,
        fullFrontendId,
        description: description as ConditionViolationsStatus['description'],
        extremeValue: Math.max(...entries.map((e) => e.extremeValue)),
        unit: entries[0].unit,
        latestDetect: new Date(
          Math.max(...entries.map((e) => e.latestDetect.getTime())),
        ),
        duration: Math.max(...entries.map((e) => e.duration)),
        affectedUnits: entries.reduce((sum, e) => sum + e.affectedUnits, 0),
      });
    }
  }

  return results;
}
